import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getShortAnswer,
  updateShortAnswer,
} from '../service';
import { fetchSubjectsByState } from '../utils/subjects';
import { toolSettingsPatch } from '../utils/toolSettingsPatch';
import { useUserStates } from './useUserStates';

// Helper to convert subject codes to subject objects using subjectsList
const mapCodesToSubjects = (codes, subjectsList) => {
  if (!Array.isArray(codes) || codes.length === 0) return [];
  return codes.map((code) => {
    const codeStr = typeof code === 'object' ? (code.subjectCode || code.code) : code;
    // Try by code first, then by name (codeStr might be a name from legacy data)
    const found = subjectsList.find((s) => (s.subjectCode || s.code) === codeStr)
      || subjectsList.find((s) =>
        String(s.subjectName || s.name || s.title || '').toLowerCase().trim() === String(codeStr).toLowerCase().trim()
      );
    if (found) {
      return { subjectCode: found.subjectCode || found.code, subjectName: found.subjectName || found.name || found.title };
    }
    // Subject not found in subjectsList — return as-is if it's already an object
    if (typeof code === 'object' && code.subjectCode && code.subjectName) return code;
    return null;
  }).filter(Boolean);
};

const filterValidCriterias = (criterias) => {
  if (!criterias || !Array.isArray(criterias)) return [];
  return criterias.filter(c => {
    if (!c) return false;

    const hasCriteriaText = c.criteria && c.criteria.trim() !== '';
    const hasMarks = c.marks !== undefined && c.marks !== null && c.marks > 0;
    const hasId = c.id !== undefined && c.id !== null;
    return hasCriteriaText || hasMarks || hasId;
  });
};

// Strips only null entries but keeps incomplete rows so they are not silently removed
const filterNullCriterias = (criterias) => {
  if (!criterias || !Array.isArray(criterias)) return [];
  return criterias.filter(c => c != null);
};

const sortCriteriasByMarksDesc = (criterias) => {
  if (!criterias || !Array.isArray(criterias)) return [];
  return [...criterias].sort((a, b) => (b.marks ?? 0) - (a.marks ?? 0));
};

const withSortedCriterias = (answer) => {
  if (!answer) return answer;
  return {
    ...answer,
    criterias: sortCriteriasByMarksDesc(answer.criterias)
  };
};

const enrichWithMetadata = (item) => {
  if (!item) return item;
  const metadata = item.metadata || {};
  return {
    ...item,
    years: Array.isArray(metadata.years) ? metadata.years : [],
    subjects: Array.isArray(metadata.subjects) ? metadata.subjects : [],
    taskTypes: Array.isArray(metadata.taskTypes) ? metadata.taskTypes : [],
    responseTypes: Array.isArray(metadata.responseTypes) ? metadata.responseTypes : [],
  };
};

// Re-attach _clientId tags from the pre-save criterias onto the post-save
// response. Without this, the component's rowKey flips from `cid:<x>` to
// `id:<y>` when a new row first gets persisted, which orphans the local-state
// maps that were keyed by the draft id (localCriterias, expandedRangeRows,
// validationErrors). Matching priority:
//   1. id (most precise — row already had an id)
//   2. content signature (new row first save: no shared id yet)
// Each previous row can only be consumed once so two blank drafts with the
// same content don't both steal the same _clientId.
const mergeClientIds = (responseCriterias, previousCriterias) => {
  if (!Array.isArray(responseCriterias)) return responseCriterias;
  if (!Array.isArray(previousCriterias) || previousCriterias.length === 0) {
    return responseCriterias;
  }
  const consumed = new Set();
  const findMatch = (candidate, matcher) => {
    for (let i = 0; i < previousCriterias.length; i += 1) {
      if (consumed.has(i)) continue;
      const prev = previousCriterias[i];
      if (prev && prev._clientId && matcher(prev, candidate)) {
        consumed.add(i);
        return prev._clientId;
      }
    }
    return null;
  };
  return responseCriterias.map((c) => {
    if (!c || c._clientId) return c;
    const byId = c.id
      ? findMatch(c, (prev) => prev.id === c.id)
      : null;
    if (byId) return { ...c, _clientId: byId };
    const byContent = findMatch(
      c,
      (prev) =>
        !prev.id
        && (prev.criteria ?? '') === (c.criteria ?? '')
        && (prev.marks ?? null) === (c.marks ?? null)
        && (prev.marksFrom ?? null) === (c.marksFrom ?? null)
    );
    if (byContent) return { ...c, _clientId: byContent };
    return c;
  });
};

const computeHighestMarks = (criterias) =>
  (criterias || []).reduce((max, c) => {
    const marks = Number(c?.marks);
    return Number.isFinite(marks) && marks > max ? marks : max;
  }, 0);

const useShortAnswer = (shortAnswerId) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { states: userStates } = useUserStates();
  const userState = userStates;
  const [shortAnswer, setShortAnswer] = useState(null);
  const [subjectsList, setSubjectsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Click handlers captured at render-time close over a stale `shortAnswer`.
  // When a handler awaits an in-flight save and then invokes a mutator here,
  // we need the mutator to read the LATEST state, not the pre-save snapshot
  // the handler captured. Updating the ref synchronously in `applyShortAnswer`
  // (rather than via useEffect, which only fires after the DOM commits) means
  // the ref is fresh as soon as a setState call is made, before any awaited
  // promise resumes.
  const shortAnswerRef = useRef(null);
  const newOwner = () => ({ id: shortAnswerId, tail: Promise.resolve(), pending: 0 });
  const activeIdRef = useRef(null);
  if (activeIdRef.current?.id !== shortAnswerId) activeIdRef.current = newOwner();
  const owner = activeIdRef.current;
  const applyShortAnswer = (next) => {
    if (activeIdRef.current !== owner) return;
    const subjects = next?.subjects;
    const normalized = subjectsList.length && subjects?.some(subject => typeof subject === 'string')
      ? { ...next, subjects: mapCodesToSubjects(subjects, subjectsList) }
      : next;
    shortAnswerRef.current = normalized;
    setShortAnswer(normalized);
  };
  useEffect(() => {
    // Keep the ref sync'd for any state updates that skip applyShortAnswer
    // (there should be none, but belt-and-braces against regressions).
    shortAnswerRef.current = shortAnswer;
  }, [shortAnswer]);

  // Fetch subjects for metadata building
  useEffect(() => {
    const loadSubjects = async () => {
      if (userState.length > 0) {
        const subjects = await fetchSubjectsByState(userState);
        setSubjectsList(subjects);
      }
    };
    loadSubjects();
  }, [JSON.stringify(userState)]);

  useEffect(() => {
    let cancelled = false;
    const fetchShortAnswer = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getShortAnswer(shortAnswerId);
        if (cancelled) return;

        if (!data || (Array.isArray(data) && data.length === 0)) {
          throw new Error('Short answer not found');
        }

        // An array response must contain the requested item.
        const answer = Array.isArray(data)
          ? data.find(item => item.id === shortAnswerId || item._id === shortAnswerId)
          : data;
        if (!answer) throw new Error('Short answer not found');

        if (!answer.criterias || answer.criterias.length === 0) {
          answer.criterias = [];
        } else {
          answer.criterias = answer.criterias.filter(c => c != null);
        }

        const computedHighest = computeHighestMarks(answer.criterias);
        const currentHighest = Number(answer.highestMarks);
        if (
          !Number.isFinite(currentHighest) ||
          currentHighest !== computedHighest
        ) {
          answer.highestMarks = computedHighest;
          // Finish the marks-only backfill before opening the editor so it
          // cannot race with a teacher's newer marks. A failure leaves the
          // locally computed value available for the next explicit save.
          // A Jeddle template isn't the teacher's to write (the backend
          // answers 403), and the teacher only opened it, so a failure here
          // is never worth a toast.
          if (!answer.isSystem) {
            try {
              await updateShortAnswer(
                shortAnswerId,
                { highestMarks: computedHighest },
                { suppressToast: true }
              );
            } catch (err) {
              console.warn('Failed to backfill highestMarks:', err);
            }
          }
        }

        if (!cancelled) applyShortAnswer(withSortedCriterias(enrichWithMetadata(answer)));
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching short answer:', err);
        setError(err);
        applyShortAnswer(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (shortAnswerId) {
      fetchShortAnswer();
    }
    return () => { cancelled = true; };
  }, [shortAnswerId]);

  // Convert subject codes to subject objects when subjectsList becomes available.
  // Read from `shortAnswerRef.current` rather than the render-time `shortAnswer`
  // closure: if a save (title/criteria edit) completes between this effect's
  // trigger and its execution, the closure is stale and using it would overwrite
  // the newer persisted fields with older ones. The ref is updated synchronously
  // in applyShortAnswer, so it's always the most recent state here.
  useEffect(() => {
    if (subjectsList.length === 0) return;
    const latest = shortAnswerRef.current;
    if (!latest?.subjects?.length) return;
    const firstSubject = latest.subjects[0];
    if (typeof firstSubject !== 'string') return;
    const mappedSubjects = mapCodesToSubjects(latest.subjects, subjectsList);
    applyShortAnswer({ ...latest, subjects: mappedSubjects });
  }, [subjectsList, shortAnswer?.subjects?.length]);

  useEffect(() => {
    if (error) {
      history.push('/tools/shortanswers');
    }
  }, [error, history]);

  // Serialize every write, including content, before reading the current row.
  // Multiple blur handlers can resume together after the same earlier save.
  const enqueue = write => {
    owner.pending += 1;
    setIsSaving(true);
    const pending = owner.tail.catch(() => {}).then(() => {
      if (activeIdRef.current !== owner) throw new Error('The selected short answer changed.');
      return write();
    });
    owner.tail = pending;
    return pending.finally(() => {
      owner.pending -= 1;
      if (activeIdRef.current === owner) setIsSaving(owner.pending > 0);
    });
  };

  const updateShortAnswerTitle = updatedData => {
    return enqueue(async () => {
      const current = shortAnswerRef.current;
      if (!current || activeIdRef.current !== owner) return;
      const patch = toolSettingsPatch(updatedData, current, subjectsList);
      await updateShortAnswer(shortAnswerId, patch);
      const latest = shortAnswerRef.current;
      applyShortAnswer({ ...latest, ...updatedData, ...(patch.metadata ? { metadata: patch.metadata } : {}) });
      queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
      queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });
    });
  };

  const updateHighestMarks = (highestMarks) => enqueue(async () => {
    try {
      const current = shortAnswerRef.current;
      if (!current || activeIdRef.current !== owner) return;

      const metadata = current.metadata;

      const updated = {
        ...current,
        highestMarks: parseInt(highestMarks, 10) || 0,
        criterias: filterValidCriterias(current.criterias),
        metadata,
      };

      await updateShortAnswer(shortAnswerId, { highestMarks: updated.highestMarks });
      applyShortAnswer({ ...shortAnswerRef.current, highestMarks: updated.highestMarks });

      queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
      queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });
    } catch (err) {
      throw err;
    }
  });

  const addCriteria = (newCriteria) => enqueue(async () => {
    try {
      const current = shortAnswerRef.current;
      if (!current || activeIdRef.current !== owner) return;

      // Stamp an internal client id so an unsaved draft has a stable identity
      // while it's being edited. Survives sort-by-marks reordering and lets
      // click handlers identify the right row even if the blur-save that
      // preceded them assigned a backend id or re-sorted the list.
      const stamped = newCriteria._clientId
        ? newCriteria
        : { ...newCriteria, _clientId: `cid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
      const updatedLocal = {
        ...current,
        criterias: [...(current.criterias || []), stamped]
      };
      applyShortAnswer(withSortedCriterias(updatedLocal));

      const hasCriteriaText = newCriteria.criteria && newCriteria.criteria.trim() !== '';
      const hasMarks = newCriteria.marks !== undefined && newCriteria.marks !== null && newCriteria.marks > 0;

      if (hasCriteriaText || hasMarks) {

        const metadata = current.metadata;

        const filteredCriterias = filterValidCriterias([...(current.criterias || []), stamped]);
        const updated = {
          ...current,
          criterias: filteredCriterias,
          highestMarks: computeHighestMarks(filteredCriterias),
          metadata,
        };

        const savedResponse = await updateShortAnswer(shortAnswerId, { criterias: updated.criterias, highestMarks: updated.highestMarks });
        const response = savedResponse && { ...savedResponse, title: shortAnswerRef.current?.title, metadata: shortAnswerRef.current?.metadata };
        if (response) {
          // Preserve _clientId tags from the pre-save criterias onto the
          // response, otherwise a new row's rowKey flips cid:→id: and the
          // local-state maps (expandedRangeRows, localCriterias,
          // validationErrors) end up keyed by a stale cid: value.
          const mergedCriterias = mergeClientIds(
            response.criterias,
            updated.criterias
          );
          applyShortAnswer(
            withSortedCriterias(
              enrichWithMetadata({ ...response, criterias: mergedCriterias })
            )
          );
        }

        queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
        queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });

      }
    } catch (err) {
      throw err;
    }
  });

  // Resolve a rowKey ("id:<id>" for saved criteria, "idx:<n>" for unsaved) or
  // raw numeric index to a current array index. Using the rowKey and looking
  // up against the latest shortAnswer state avoids stale-index bugs after the
  // server re-sorts criteria by marks desc.
  const resolveCriteriaIndex = (ref, current) => {
    const src = current ?? shortAnswerRef.current;
    if (typeof ref === 'number') return ref;
    if (typeof ref !== 'string' || !src?.criterias) return -1;
    if (ref.startsWith('id:')) {
      const id = ref.slice(3);
      return src.criterias.findIndex((c) => c && c.id === id);
    }
    if (ref.startsWith('cid:')) {
      // Client-side draft id — stable across sort reorders for unsaved rows.
      const cid = ref.slice(4);
      return src.criterias.findIndex((c) => c && c._clientId === cid);
    }
    if (ref.startsWith('idx:')) {
      const idx = parseInt(ref.slice(4), 10);
      if (idx < 0 || idx >= src.criterias.length) return -1;
      return idx;
    }
    return -1;
  };

  const updateCriteria = (criteriaRef, updatedCriteria) => enqueue(async () => {
    try {
      // Read latest state via ref — handlers that awaited an in-flight save
      // may be carrying a stale shortAnswer from their render closure.
      const current = shortAnswerRef.current;
      if (!current || activeIdRef.current !== owner) return;
      const criteriaIndex = resolveCriteriaIndex(criteriaRef, current);
      if (criteriaIndex < 0 || criteriaIndex >= current.criterias.length) return;

      const updatedCriterias = current.criterias.map((criteriaItem, idx) => {
        if (idx === criteriaIndex) {
          return { ...criteriaItem, ...updatedCriteria };
        }
        return criteriaItem;
      });

      const filteredCriterias = filterValidCriterias(updatedCriterias);

      const metadata = current.metadata;

      const updated = {
        ...current,
        criterias: filteredCriterias,
        highestMarks: computeHighestMarks(filteredCriterias),
        metadata,
      };

      const savedResponse = await updateShortAnswer(shortAnswerId, { criterias: updated.criterias, highestMarks: updated.highestMarks });
      const response = savedResponse && { ...savedResponse, title: shortAnswerRef.current?.title, metadata: shortAnswerRef.current?.metadata };
      // Preserve _clientId tags through the save round-trip so rowKeys stay
      // stable across the first persist of a draft row (see mergeClientIds).
      const next = response
        ? { ...response, criterias: mergeClientIds(response.criterias, updated.criterias) }
        : updated;
      applyShortAnswer(withSortedCriterias(enrichWithMetadata({ ...next, title: shortAnswerRef.current?.title, metadata: shortAnswerRef.current?.metadata })));

      queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
      queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });

    } catch (err) {
      throw err;
    }
  });

  const deleteCriteria = (criteriaRef) => enqueue(async () => {
    try {
      const current = shortAnswerRef.current;
      if (!current || activeIdRef.current !== owner) return;
      const criteriaIndex = resolveCriteriaIndex(criteriaRef, current);
      if (criteriaIndex < 0 || criteriaIndex >= current.criterias.length) return;

      const remainingCriterias = current.criterias.filter((_, idx) => idx !== criteriaIndex);
      const filteredCriterias = filterValidCriterias(remainingCriterias);

      const metadata = current.metadata;

      const updated = {
        ...current,
        criterias: filteredCriterias,
        highestMarks: computeHighestMarks(filteredCriterias),
        metadata,
      };

      const savedResponse = await updateShortAnswer(shortAnswerId, { criterias: updated.criterias, highestMarks: updated.highestMarks });
      const response = savedResponse && { ...savedResponse, title: shortAnswerRef.current?.title, metadata: shortAnswerRef.current?.metadata };
      // Preserve _clientId tags for any sibling drafts that weren't deleted.
      const next = response
        ? { ...response, criterias: mergeClientIds(response.criterias, updated.criterias) }
        : updated;
      applyShortAnswer(withSortedCriterias(enrichWithMetadata({ ...next, title: shortAnswerRef.current?.title, metadata: shortAnswerRef.current?.metadata })));

      queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
      queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });

    } catch (err) {
      throw err;
    }
  });

  // Clears marksFrom on every criterion in a single PATCH so the teacher can
  // toggle mark-ranges off without sequential updateCriteria calls (which
  // would fight the sort-by-marks-desc re-indexing). Blank drafts (no id,
  // no text, no marks) are preserved in local state so in-flight new rows
  // survive the clear.
  const clearAllMarkRanges = () => enqueue(async () => {
    // Always read latest state via ref: callers may have awaited a blur-save
    // that has already mutated shortAnswer, but their captured reference is
    // pre-save. Reading the ref here prevents us from PATCHing stale marks
    // or criteria text back over the just-saved values.
    const current = shortAnswerRef.current;
    if (!current || activeIdRef.current !== owner) return;
    const hasAny = current.criterias?.some(
      (c) => c.marksFrom !== undefined && c.marksFrom !== null
    );
    if (!hasAny) return;

    try {
      const clearedCriterias = (current.criterias || []).map((c) => ({
        ...c,
        marksFrom: null,
      }));
      const patchCriterias = filterValidCriterias(clearedCriterias);

      const metadata = current.metadata;

      const patchBody = { ...current, criterias: patchCriterias, metadata };
      const savedResponse = await updateShortAnswer(shortAnswerId, { criterias: patchBody.criterias, highestMarks: patchBody.highestMarks });
      const response = savedResponse && { ...savedResponse, title: shortAnswerRef.current?.title, metadata: shortAnswerRef.current?.metadata };

      const rawServerCriterias = response?.criterias || patchCriterias;
      // Re-attach the _clientId tags we had on the pre-save rows so the
      // component's rowKeys stay stable across this clear.
      const serverCriterias = mergeClientIds(rawServerCriterias, patchCriterias);
      // Re-attach only truly blank drafts (no id, no text, zero-ish marks) so
      // a teacher's in-flight "Add Criteria" row doesn't vanish.
      const localBlanks = clearedCriterias.filter(
        (c) =>
          !c.id &&
          !(c.criteria && c.criteria.trim() !== '') &&
          !(c.marks !== undefined && c.marks !== null && c.marks > 0)
      );
      const mergedCriterias = [...serverCriterias, ...localBlanks];
      applyShortAnswer(
        withSortedCriterias(
          enrichWithMetadata({
            ...(response || current),
            title: shortAnswerRef.current?.title,
            metadata: shortAnswerRef.current?.metadata,
            criterias: mergedCriterias,
          })
        )
      );

      queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
      queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });
    } catch (err) {
      throw err;
    }
  });

  // Read-only accessor to the latest shortAnswer, for callers (e.g. click
  // handlers that awaited an in-flight save) that need to re-check state
  // after their render closure became stale.
  const getCurrentShortAnswer = () => shortAnswerRef.current;

  return {
    shortAnswer,
    isLoading,
    isSaving,
    error,
    updateShortAnswerTitle,
    updateHighestMarks,
    addCriteria,
    updateCriteria,
    deleteCriteria,
    clearAllMarkRanges,
    getCurrentShortAnswer,
  };
};

export default useShortAnswer;
